import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search,
  Building2,
  Users,
  Trees,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Mail,
  Phone,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { companiesService } from '@/services';
import type { ApiCompany, CreateCompanyRequest, UpdateCompanyRequest } from '@/types/api';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { CompanyDeleteDialog } from '@/components/companies/CompanyDeleteDialog';
import { useToast } from '@/hooks/use-toast';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Componente de skeleton para loading
function CompanyCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex items-center gap-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-24" />
        </div>
        <div className="pt-3 border-t border-border">
          <Skeleton className="h-3 w-32" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Companies() {
  const [searchTerm, setSearchTerm] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<ApiCompany | undefined>();
  const [companyToDelete, setCompanyToDelete] = useState<ApiCompany | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para obtener empresas
  const { 
    data: companiesData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['companies', searchTerm],
    queryFn: async () => {
      const response = await companiesService.getAll({ 
        search: searchTerm || undefined,
        pageSize: 100 
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
  });

  // Mutation para crear empresa
  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyRequest) => companiesService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast({
          title: 'Empresa creada',
          description: `La empresa ha sido creada correctamente.`,
        });
        setFormDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al crear la empresa',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para actualizar empresa
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyRequest }) => 
      companiesService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast({
          title: 'Empresa actualizada',
          description: `La empresa ha sido actualizada correctamente.`,
        });
        setFormDialogOpen(false);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al actualizar la empresa',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para eliminar empresa
  const deleteMutation = useMutation({
    mutationFn: (id: string) => companiesService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        toast({
          title: 'Empresa eliminada',
          description: `La empresa ha sido eliminada.`,
          variant: 'destructive',
        });
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
      } else {
        toast({
          title: 'Error',
          description: response.error?.message || 'Error al eliminar la empresa',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const companies = companiesData?.items || [];

  const handleNewCompany = () => {
    setSelectedCompany(undefined);
    setFormDialogOpen(true);
  };

  const handleEditCompany = (company: ApiCompany) => {
    setSelectedCompany(company);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (company: ApiCompany) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = (data: { name: string; cif: string; address?: string; phone?: string; email?: string }) => {
    if (selectedCompany) {
      updateMutation.mutate({ id: selectedCompany.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDeleteConfirm = () => {
    if (companyToDelete) {
      deleteMutation.mutate(companyToDelete.id);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Empresas</h1>
            <p className="text-muted-foreground mt-1">Gestiona las empresas y sus recursos</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <CompanyCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Empresas</h1>
            <p className="text-muted-foreground mt-1">Gestiona las empresas y sus recursos</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar empresas'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Empresas
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona las empresas y sus recursos
          </p>
        </div>
        <Button 
          onClick={handleNewCompany}
          className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Empresa
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre o CIF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Companies grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {companies.map((company) => (
          <motion.div key={company.id} variants={itemVariants}>
            <Card className="group hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                        {company.name}
                      </h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <FileText className="w-3 h-3" />
                        <span>CIF: {company.cif}</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Eye className="w-4 h-4 mr-2" />
                        Ver detalles
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEditCompany(company)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteClick(company)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact info */}
                <div className="space-y-2 text-sm">
                  {company.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span>{company.email}</span>
                    </div>
                  )}
                  {company.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{company.phone}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4">
                  <Badge variant="secondary" className="gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    {company.userCount ?? 0} usuarios
                  </Badge>
                  <Badge variant="secondary" className="gap-1.5">
                    <Trees className="w-3.5 h-3.5" />
                    {company.gardenCount ?? 0} jardines
                  </Badge>
                </div>

                {/* Date */}
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Creada el {new Date(company.createdAt).toLocaleDateString('es-ES')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {companies.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No se encontraron empresas.
          </p>
        </motion.div>
      )}

      {/* Dialogs */}
      <CompanyFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        company={selectedCompany ? {
          id: selectedCompany.id,
          name: selectedCompany.name,
          cif: selectedCompany.cif,
          address: selectedCompany.address,
          phone: selectedCompany.phone,
          email: selectedCompany.email,
          createdAt: new Date(selectedCompany.createdAt),
          updatedAt: new Date(selectedCompany.updatedAt),
        } : undefined}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <CompanyDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        company={companyToDelete ? {
          id: companyToDelete.id,
          name: companyToDelete.name,
          cif: companyToDelete.cif,
          address: companyToDelete.address,
          phone: companyToDelete.phone,
          email: companyToDelete.email,
          createdAt: new Date(companyToDelete.createdAt),
          updatedAt: new Date(companyToDelete.updatedAt),
        } : null}
        onConfirm={handleDeleteConfirm}
        userCount={companyToDelete?.userCount ?? 0}
        gardenCount={companyToDelete?.gardenCount ?? 0}
        isLoading={deleteMutation.isPending}
      />
    </motion.div>
  );
}
